import { MessageSquareWarning } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import { useReviewTranslation } from "@/domains/reviews/hooks/use-review-translation";
import { SalonReviewRow } from "@/domains/settings/features/reviews/components/salon-review-row";
import type { ReviewFilterIntent } from "@/domains/settings/features/reviews/hooks/use-review-filter-intent";
import { useSalonReviews } from "@/domains/settings/features/reviews/hooks/use-salon-reviews";
import { useSalonStore } from "@/lib/store/salon";

const PREVIEW_LIMIT = 5;

/**
 * Lands the destination page on the unreplied set immediately — otherwise
 * the user would have to find and click the chip to match what they were
 * just looking at.
 */
const VIEW_ALL_INTENT: ReviewFilterIntent = { replyFilter: "unreplied" };

/**
 * Awaiting-reply triage block on the Customer Service overview.
 * Renders as a flat section (no outer Card) below the Customer Feedback
 * card — individual review rows still carry their own Card styling via
 * `SalonReviewRow`, so the list reads as a stack of review-row cards
 * under a section header.
 *
 * Surfaces Google reviews still missing an owner reply, limited to 1–3★
 * to align with the Customer Service nav badge count. 4–5★ unreplied
 * reviews (happy customers leaving praise) are intentionally excluded
 * so the triage card stays focused on service-recovery cases. Lowest
 * rating first. Manual (Platform) reviews are excluded — backend implies
 * `source: GOOGLE` when `unrepliedOnly` is true.
 *
 * Returns `null` when there's nothing to act on so the page stays calm
 * in the happy state.
 */
export function AwaitingReplySection() {
  const { t } = useTranslation("reviews");
  const { currentSalon } = useSalonStore();
  const { reviews, loading, hasMore } = useSalonReviews({
    limitOverride: PREVIEW_LIMIT,
    sort: "LOWEST_FIRST",
    unrepliedOnly: true,
    ignoreFilters: true,
    maxStarRating: 3,
  });
  const { translateInBackground } = useReviewTranslation();

  useEffect(() => {
    if (currentSalon && reviews.length > 0) {
      translateInBackground(reviews, currentSalon.id);
    }
  }, [currentSalon, reviews, translateInBackground]);

  if (loading && reviews.length === 0) {
    return (
      <div className="flex items-center justify-center py-4">
        <Spinner size="sm" />
      </div>
    );
  }

  if (reviews.length === 0) return null;

  return (
    <div className="space-y-3">
      {/* Header + description nudged slightly right so they don't cling
          to the page edge. Review rows below keep their full-width left
          alignment — only the heading group is inset. */}
      <div className="pl-1.5 space-y-1">
        <div className="flex items-center gap-2">
          <MessageSquareWarning className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          <h2 className="text-base font-semibold">{t("awaitingReply.title")}</h2>
          <Badge variant="destructive" className="ml-1">
            {hasMore ? `${reviews.length}+` : reviews.length}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">{t("awaitingReply.description")}</p>
      </div>

      <div className="space-y-2">
        {reviews.map((review) => (
          <SalonReviewRow key={review.id} review={review} />
        ))}
      </div>

      {hasMore ? (
        <div className="flex justify-end pt-1 text-sm">
          <Link
            to="/customer-service/google-business"
            state={VIEW_ALL_INTENT}
            className="text-muted-foreground hover:text-foreground"
          >
            {t("awaitingReply.viewAll")}
          </Link>
        </div>
      ) : null}
    </div>
  );
}
